import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

// Single source of truth for every permission-gated page in the sidebar.
// Reused by pages/UserManagement.js to build the permission checkboxes.
export const MASTER_ENTRY_PAGES = [
  { path: '/training-title', icon: 'fas fa-chalkboard-teacher', label: 'Training Title' },
  { path: '/batchno', icon: 'fas fa-layer-group', label: 'Batch No' },
  { path: '/organization-details', icon: 'fas fa-building', label: 'Organization Details' },
  { path: '/company-info', icon: 'fas fa-industry', label: 'Company Info' },
  { path: '/trainers-name', icon: 'fas fa-user-tie', label: 'Trainers Name' },
  { path: '/work-location', icon: 'fas fa-map-marker-alt', label: 'Work Location' },
  { path: '/projectname', icon: 'fas fa-city', label: 'Project Name' },
  { path: '/drivinglicense', icon: 'fas fa-id-card', label: 'Driving License' },
];

export const REGISTRATION_PAGES = [
  { path: '/candidate-registration', icon: 'fas fa-user-plus', label: 'Registration List' },
  { path: '/pre-evaluation-test', icon: 'fas fa-file-signature', label: 'Pre Evaluation Test' },
  { path: '/post-evaluation-test', icon: 'fas fa-file-signature', label: 'Post Evaluation Test' },
];

export const EVALUATION_PAGES = [
  { path: '/participent-feedback', icon: 'fas fa-comment-dots', label: 'Participant Feedback' },
  { path: '/personal-interaction-evaluation', icon: 'fas fa-user-check', label: 'Personal Evaluations' },
  { path: '/evaluation-form', icon: 'fas fa-clipboard-list', label: 'Evaluation Form' },
  { path: '/view-evaluation-form', icon: 'fas fa-search', label: 'View Evaluation' },
];

export const DOCUMENT_PAGES = [
  { path: '/certificate-print', icon: 'fas fa-certificate', label: 'Certificate Print' },
  { path: '/renewal', icon: 'fas fa-redo-alt', label: 'Renewal' },
  { path: '/attendance-sheet', icon: 'fas fa-print', label: 'Attendance Sheet' },
];

export const REPORT_PAGES = [
  { path: '/batch-report', icon: 'fas fa-users', label: 'Batch Report' },
  { path: '/overall-report', icon: 'fas fa-clipboard-list', label: 'Overall Report' },
  { path: '/monthly-report', icon: 'fas fa-calendar-alt', label: 'Monthly Report' },
  { path: '/consolidated-report', icon: 'fas fa-layer-group', label: 'Consolidated Report' },
];

// Every permission-gated page, flattened - used for the User Management
// permission checkboxes and for ProtectedRoute's access check.
export const ALL_PERMISSION_PAGES = [
  ...MASTER_ENTRY_PAGES,
  ...REGISTRATION_PAGES,
  ...EVALUATION_PAGES,
  ...DOCUMENT_PAGES,
  ...REPORT_PAGES,
];

export const ROLE_ADMIN = 1;

// Always accessible regardless of role/permissions - self-service pages that
// every logged-in user needs, and aren't part of the permission checkbox list.
export const ALWAYS_ALLOWED_PAGES = ['/dashboard', '/profile', '/changepassword'];

export const getStoredRole = () => Number(localStorage.getItem('role'));
export const getStoredPermissions = () => {
  const raw = localStorage.getItem('permissions');
  return raw ? JSON.parse(raw) : null; // null = no restriction (full access)
};
export const canAccessPage = (path) => {
  if (ALWAYS_ALLOWED_PAGES.includes(path)) return true;
  if (getStoredRole() === ROLE_ADMIN) return true;
  const permissions = getStoredPermissions();
  if (!permissions) return true; // no restriction configured yet
  return permissions.includes(path);
};

const Sidebar = () => {
  const Name = localStorage.getItem('name');
  const role = getStoredRole();
  const permissions = getStoredPermissions();
  const location = useLocation();

  const [masterEntryOpen, setMasterEntryOpen] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [evaluationOpen, setEvaluationOpen] = useState(false);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => {
    const masterRoutes = MASTER_ENTRY_PAGES.map((p) => p.path);
    const regRoutes = REGISTRATION_PAGES.map((p) => p.path);
    const evaluationRoutes = EVALUATION_PAGES.map((p) => p.path);
    const documentRoutes = DOCUMENT_PAGES.map((p) => p.path);
    const reportRoutes = REPORT_PAGES.map((p) => p.path);

    if (masterRoutes.includes(location.pathname)) setMasterEntryOpen(true);
    if (regRoutes.includes(location.pathname)) setRegistrationOpen(true);
    if (evaluationRoutes.includes(location.pathname)) setEvaluationOpen(true);
    if (documentRoutes.includes(location.pathname)) setDocumentsOpen(true);
    if (reportRoutes.includes(location.pathname)) setReportOpen(true);
  }, [location.pathname]);

  const toggleMenu = (setMenu, current) => setMenu(!current);
  const isActive = (path) => location.pathname === path;
  const canSee = (path) => role === ROLE_ADMIN || !permissions || permissions.includes(path);

  const navLinkStyle = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 16px',
    margin: '2px 10px',
    borderRadius: '10px',
    color: active ? '#ffffff' : '#b9b6d3',
    background: active ? 'linear-gradient(135deg, #4f46e5, #4338ca)' : 'transparent',
    fontWeight: active ? 600 : 500,
    fontSize: '0.88rem',
    textDecoration: 'none',
    boxShadow: active ? '0 4px 14px rgba(79, 70, 229, 0.35)' : 'none',
    transition: 'background 0.15s ease, color 0.15s ease',
  });

  const groupHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    margin: '2px 10px',
    borderRadius: '10px',
    color: '#b9b6d3',
    fontWeight: 500,
    fontSize: '0.88rem',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const sectionLabelStyle = {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#6f6c8c',
    padding: '18px 20px 6px',
  };

  const chevron = (open) => (
    <i
      className="fas fa-chevron-right"
      style={{
        fontSize: '0.65rem',
        transition: 'transform 0.2s ease',
        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
      }}
    ></i>
  );

  const NavItem = ({ to, icon, label }) => (
    <li className="nav-item" style={{ listStyle: 'none' }}>
      <Link to={to} style={navLinkStyle(isActive(to))}>
        <i className={icon} style={{ width: '18px', textAlign: 'center', fontSize: '0.95rem' }}></i>
        <span>{label}</span>
      </Link>
    </li>
  );

  const GroupHeader = ({ icon, label, open, onClick }) => (
    <div style={groupHeaderStyle} onClick={onClick}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <i className={icon} style={{ width: '18px', textAlign: 'center', fontSize: '0.95rem' }}></i>
        <span>{label}</span>
      </span>
      {chevron(open)}
    </div>
  );

  const visibleMasterEntry = MASTER_ENTRY_PAGES.filter((p) => canSee(p.path));
  const visibleRegistration = REGISTRATION_PAGES.filter((p) => canSee(p.path));
  const visibleEvaluation = EVALUATION_PAGES.filter((p) => canSee(p.path));
  const visibleDocuments = DOCUMENT_PAGES.filter((p) => canSee(p.path));
  const visibleReports = REPORT_PAGES.filter((p) => canSee(p.path));

  return (
    <aside
      className="main-sidebar elevation-3"
      style={{
        background: 'linear-gradient(180deg, #211f36 0%, #1a1830 100%)',
        minHeight: '100vh',
        border: 'none',
      }}
    >
      {/* Brand */}
      <Link
        to="/"
        className="brand-link d-flex align-items-center"
        style={{
          height: '64px',
          padding: '0 20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          textDecoration: 'none',
        }}
      >
        <span
          style={{
            width: '34px',
            height: '34px',
            borderRadius: '9px',
            background: 'linear-gradient(135deg, #4f46e5, #818cf8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.95rem',
            flexShrink: 0,
          }}
        >
          EW
        </span>
        <span className="brand-text" style={{ marginLeft: '12px', color: '#fff', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.2 }}>
          Eazyworld EHS
          <br />
          <span style={{ fontWeight: 400, fontSize: '0.72rem', color: '#a5a2c4' }}>
            {Name ? `Hi, ${Name}` : 'Welcome back'}
          </span>
        </span>
      </Link>

      {/* Sidebar Content */}
      <div className="sidebar" style={{ paddingBottom: '24px' }}>
        <nav className="mt-2">
          <ul className="nav flex-column" style={{ padding: 0, margin: 0 }} data-widget="treeview" role="menu" data-accordion="false">

            <div style={{ padding: '14px 10px 4px' }}>
              <NavItem to="/" icon="fas fa-tachometer-alt" label="Dashboard" />
            </div>

            {role === ROLE_ADMIN && (
              <>
                <div style={sectionLabelStyle}>Administration</div>
                <div style={{ padding: '0 10px' }}>
                  <NavItem to="/user-management" icon="fas fa-users-cog" label="User Management" />
                </div>
              </>
            )}

            {(visibleMasterEntry.length > 0 || visibleRegistration.length > 0) && (
              <div style={sectionLabelStyle}>Manage</div>
            )}

            {/* Master Entry */}
            {visibleMasterEntry.length > 0 && (
              <li className="nav-item" style={{ listStyle: 'none' }}>
                <GroupHeader
                  icon="fas fa-layer-group"
                  label="Master Entry"
                  open={masterEntryOpen}
                  onClick={() => toggleMenu(setMasterEntryOpen, masterEntryOpen)}
                />
                {masterEntryOpen && (
                  <ul style={{ padding: 0, margin: '2px 0 4px' }}>
                    {visibleMasterEntry.map((item) => (
                      <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                    ))}
                  </ul>
                )}
              </li>
            )}

            {/* Registration */}
            {visibleRegistration.length > 0 && (
              <li className="nav-item" style={{ listStyle: 'none' }}>
                <GroupHeader
                  icon="fas fa-th-list"
                  label="Registration"
                  open={registrationOpen}
                  onClick={() => toggleMenu(setRegistrationOpen, registrationOpen)}
                />
                {registrationOpen && (
                  <ul style={{ padding: 0, margin: '2px 0 4px' }}>
                    {visibleRegistration.map((item) => (
                      <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                    ))}
                  </ul>
                )}
              </li>
            )}

            {/* Evaluation */}
            {visibleEvaluation.length > 0 && (
              <>
                <div style={sectionLabelStyle}>Evaluation</div>
                <li className="nav-item" style={{ listStyle: 'none' }}>
                  <GroupHeader
                    icon="fas fa-user-check"
                    label="Evaluation"
                    open={evaluationOpen}
                    onClick={() => toggleMenu(setEvaluationOpen, evaluationOpen)}
                  />
                  {evaluationOpen && (
                    <ul style={{ padding: 0, margin: '2px 0 4px' }}>
                      {visibleEvaluation.map((item) => (
                        <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                      ))}
                    </ul>
                  )}
                </li>
              </>
            )}

            {/* Documents */}
            {visibleDocuments.length > 0 && (
              <>
                <div style={sectionLabelStyle}>Documents</div>
                <li className="nav-item" style={{ listStyle: 'none' }}>
                  <GroupHeader
                    icon="fas fa-certificate"
                    label="Documents"
                    open={documentsOpen}
                    onClick={() => toggleMenu(setDocumentsOpen, documentsOpen)}
                  />
                  {documentsOpen && (
                    <ul style={{ padding: 0, margin: '2px 0 4px' }}>
                      {visibleDocuments.map((item) => (
                        <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                      ))}
                    </ul>
                  )}
                </li>
              </>
            )}

            {/* Reports */}
            {visibleReports.length > 0 && (
              <>
                <div style={sectionLabelStyle}>Reports</div>
                <li className="nav-item" style={{ listStyle: 'none' }}>
                  <GroupHeader
                    icon="fas fa-chart-bar"
                    label="Reports"
                    open={reportOpen}
                    onClick={() => toggleMenu(setReportOpen, reportOpen)}
                  />
                  {reportOpen && (
                    <ul style={{ padding: 0, margin: '2px 0 4px' }}>
                      {visibleReports.map((item) => (
                        <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
                      ))}
                    </ul>
                  )}
                </li>
              </>
            )}

          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
